import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Rocket, Zap, Users, Quote, CheckCircle } from "lucide-react";

const AboutUsPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  const sections = [
    {
      title: "Our Mission",
      icon: Rocket,
      content:
        "We created this platform to solve a common problem: finding reliable, affordable used parts shouldn't be a hassle. With help from AI, we connect part seekers with trusted businesses in seconds—saving time, money, and stress."
    },
    {
      title: "What We Do",
      icon: Zap,
      points: [
        {
          text: "Help customers find the exact part they need using AI search",
          icon: CheckCircle
        },
        {
          text: "Give sellers tools to list parts, manage inventory, and get matched with buyers",
          icon: CheckCircle
        },
        {
          text: "Create a trusted marketplace that makes used parts simple and accessible",
          icon: CheckCircle
        }
      ]
    },
    {
      title: "Why Trust Us",
      icon: Users,
      points: [
        {
          text: "Verified sellers with transparent listings",
          icon: CheckCircle
        },
        { text: "Real-time AI matching for accuracy", icon: CheckCircle },
        {
          text: "Secure communication between buyers and sellers",
          icon: CheckCircle
        }
      ]
    }
  ];

  return (
    <motion.div
      className="space-y-12 w-full"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div className="text-center" variants={itemVariants}>
        <h1 className="text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text pb-2">
          Driven by Innovation. Powered by Parts.
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
          Welcome to PartsLokator — the smart way to buy and sell used auto
          parts.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {sections.map((section, index) => (
          <motion.div key={index} variants={itemVariants}>
            <Card className="h-full border hover:shadow-sm transition-shadow duration-300 bg-card/80 backdrop-blur-sm hover:border-primary hover:scale-105 hover:shadow-white">
              <CardHeader className="items-center text-center">
                <section.icon className="h-10 w-10 mb-3 text-primary" />
                <CardTitle className="text-2xl">{section.title}</CardTitle>
              </CardHeader>
              <CardContent className="text-center text-muted-foreground">
                {section.content && <p>{section.content}</p>}
                {section.points && (
                  <ul className="space-y-3 text-left">
                    {section.points.map((point, pIndex) => (
                      <li key={pIndex} className="flex items-start gap-2">
                        <point.icon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{point.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.div
        className="text-center max-w-2xl mx-auto p-6 bg-gradient-to-r from-primary/10 via-transparent to-accent/10 rounded-lg border border-border"
        variants={itemVariants}
      >
        <Quote className="h-8 w-8 text-primary mx-auto mb-4 transform scale-x-[-1]" />
        <p className="text-xl italic text-foreground font-medium">
          “We believe in smarter auto repairs and stronger local businesses.”
        </p>
      </motion.div>
    </motion.div>
  );
};

export default AboutUsPage;
